import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { CardioIcon, CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { GymIcon, GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { WeightliftingIcon, WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { word } from "@modules/core/utils/i18n"
import { useCallback } from "react"

export enum SessionTagEnum {
  gymnasticTag = "gymnasticTag",
  cardioTag = "cardioTag",
  weightliftingTag = "weightliftingTag",
}

type SessionTagInputProps = {
  selectedTags: { key: SessionTagEnum; value: boolean }[]
  onChangeSelectedTags: (tag: string, value: boolean) => void
  className?: string
} & React.ComponentProps<"div">

export function SessionTagInput({ selectedTags, onChangeSelectedTags, className, ...props }: SessionTagInputProps) {
  const isTagSelected = useCallback(
    (tag: SessionTagEnum) => selectedTags.some((t) => t.key === tag && t.value),
    [selectedTags],
  )

  return (
    <div className={cn("card flex w-full items-stretch justify-between gap-6 p-4", className)} {...props}>
      {Object.values(SessionTagEnum).map((tag) => (
        <SessionTag
          key={tag}
          tag={tag as SessionTagEnum}
          isSelected={isTagSelected(tag as SessionTagEnum)}
          onSelect={() => onChangeSelectedTags(tag.toString(), !isTagSelected(tag as SessionTagEnum))}
        />
      ))}
    </div>
  )
}

type SessionTagProps = {
  tag: SessionTagEnum
  isSelected: boolean
  onSelect: () => void
  className?: string
} & React.ComponentProps<"div">

function SessionTag({ tag, isSelected, onSelect, className, ...props }: SessionTagProps) {
  const { theme } = useTheme()
  const themeSpecificClass =
    theme === Theme.OwnerDark
      ? `${isSelected ? "border-borderPrimary" : "border-borderDefault"}`
      : `${isSelected ? "bg-surface-background" : "border-borderDefault"} hover:border-borderDefault`

  return (
    <div
      className={cn(
        "flex flex-1 cursor-pointer flex-col items-center justify-between gap-1 rounded-2xl border py-4 transition-all duration-200 hover:bg-neutral-200",
        themeSpecificClass,
        className,
      )}
      onClick={onSelect}
      {...props}
    >
      {tag === SessionTagEnum.gymnasticTag ? (
        <>
          {isSelected ? <GymSelectedIcon color="" width={25} /> : <GymIcon color={""} width={25} />}
          <Text fontStyle="BM" className="text-textDefault-caption">
            {word("program.planning.create.gymnastique")}
          </Text>
        </>
      ) : tag === SessionTagEnum.weightliftingTag ? (
        <>
          {isSelected ? (
            <WeightliftingSelectedIcon color={""} width={25} />
          ) : (
            <WeightliftingIcon color={""} width={25} />
          )}
          <Text fontStyle="BM" className="text-textDefault-caption">
            {word("program.planning.create.weightlifting")}
          </Text>
        </>
      ) : tag === SessionTagEnum.cardioTag ? (
        <>
          {isSelected ? <CardioSelectedIcon color={""} width={25} /> : <CardioIcon color={""} width={25} />}
          <Text fontStyle="BM" className="text-textDefault-caption">
            {word("program.planning.create.cardio")}
          </Text>
        </>
      ) : null}
    </div>
  )
}
